import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { AuthModal } from "@/components/ui/auth-modal";
import { CreditsModal } from "@/components/ui/credits-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { T2V_MODEL } from "@/config/models";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { Loader2, Video, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackViewContent } from "@/services/analytics";

const MODEL = T2V_MODEL;

const ASPECT_RATIOS = [
  { value: "16:9", label: "16:9", desc: "Landscape" },
  { value: "9:16", label: "9:16", desc: "Portrait" },
  { value: "1:1", label: "1:1", desc: "Square" },
];

const DRAFT_KEY = "couchstack_draft_t2v";

function saveDraft(prompt: string, aspectRatio: string) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ prompt, aspectRatio })); } catch {}
}
function loadDraft(): { prompt: string; aspectRatio?: string } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.prompt) return parsed;
  } catch {}
  return null;
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
}

export function TextToVideoPage() {
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  useEffect(() => {
    trackViewContent("Text to Video", user?.id, { funnel_stage: "create" });
  }, []);
  const { credits } = useUserStore();
  const generateMutation = useGenerate();
  const [searchParams] = useSearchParams();
  const queryPrompt = searchParams.get("prompt");
  const draft = queryPrompt ? null : loadDraft();
  const [prompt, setPrompt] = useState(queryPrompt || draft?.prompt || "");
  const [aspectRatio, setAspectRatio] = useState(draft?.aspectRatio || "16:9");
  const [showAuth, setShowAuth] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  useEffect(() => {
    if (prompt) saveDraft(prompt, aspectRatio);
  }, [prompt, aspectRatio]);

  const canGenerate = prompt.trim().length > 0;

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;
    if (credits < MODEL.creditCost) {
      setShowCreditsModal(true);
      return;
    }
    try {
      const result = await generateMutation.mutateAsync({
        model_id: MODEL.id,
        prompt: prompt.trim(),
        aspect_ratio: aspectRatio,
      });
      clearDraft();
      if (result.video?.id) navigate(`/generation/${result.video.id}`);
    } catch (err) {
      console.error("Generation failed:", err);
    }
  }, [prompt, aspectRatio, credits, generateMutation, navigate]);

  return (
    <div className="p-5 md:p-8 max-w-4xl mx-auto">
      <div className="mb-6 animate-fade-in-up">
        <h1 className="text-2xl md:text-3xl font-extrabold text-[#2d3127] tracking-tight mb-2">
          Text to Video
        </h1>
        <p className="text-sm text-[#8a8570]">
          Describe a scene and AI will generate a 5-second video
        </p>
      </div>

      <div className="space-y-4 mb-12 animate-fade-in-up delay-100">
        <div>
          <label className="block text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="An elderly craftsman carefully shapes dough in a rustic, sunlit bakery. Close-up shot, warm tones, 4K..."
            rows={4}
            className="input-field resize-none"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">Aspect Ratio</label>
          <div className="flex gap-2">
            {ASPECT_RATIOS.map((ar) => (
              <button
                key={ar.value}
                onClick={() => setAspectRatio(ar.value)}
                className={cn(
                  "flex-1 flex flex-col items-center gap-0.5 py-2.5 rounded-xl text-sm font-medium transition-all border-2",
                  aspectRatio === ar.value
                    ? "bg-orange-50 border-orange-300/60 text-[#2d3127]"
                    : "bg-white border-[rgba(45,49,39,0.06)] text-[#8a8570] hover:text-[#5a5545] hover:border-[rgba(45,49,39,0.12)]",
                )}
              >
                <span className="text-[13px] font-semibold">{ar.label}</span>
                <span className="text-[10px] text-[#b5b0a0]">{ar.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {isSignedIn && (
          <div className="flex items-center justify-between text-xs text-[#8a8570]">
            <span className="flex items-center gap-1.5">
              <Star className="size-3 text-amber-600" />
              {MODEL.creditCost} stars · Standard
            </span>
            <span className="tabular-nums font-medium">
              Balance: <span className={credits >= MODEL.creditCost ? "text-[#2d3127]" : "text-red-500"}>{credits.toLocaleString()}</span>
            </span>
          </div>
        )}

        <Show when="signed-out">
          <button onClick={() => setShowAuth(true)} className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2">
            <Video className="size-4" />Generate Video
          </button>
          <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
        </Show>
        <Show when="signed-in">
          <button onClick={handleGenerate} disabled={!canGenerate || generateMutation.isPending} className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2">
            {generateMutation.isPending ? <><Loader2 className="size-4 animate-spin" />Generating...</> : <><Video className="size-4" />Generate Video</>}
          </button>
        </Show>

        {generateMutation.isError && (
          <NsfwOrError error={generateMutation.error} />
        )}
      </div>

      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={MODEL.creditCost}
        credits={credits}
      />
    </div>
  );
}
